import React, { useState } from 'react'

// "What the LMS sent" (rapid-api-lms D-141): the details the LMS service
// records on two kinds of timeline entry, shown on request under the entry. No
// call of its own: the session read already carries them.

const LONG = 80

const isObject = value => value !== null && typeof value === 'object'

const text = value => (typeof value === 'string' ? value : JSON.stringify(value))

// A value in the list: objects as pretty JSON, long text cut with "show all".
const Value = ({ value, long = true }) => {
  const [all, setAll] = useState(false)
  if (isObject(value)) return <pre>{JSON.stringify(value, null, 2)}</pre>
  const shown = text(value)
  if (!long || all || shown.length <= LONG) return <>{shown}</>
  return (
    <>
      {shown.slice(0, LONG)}…{' '}
      <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setAll(true)}>
        show all
      </button>
    </>
  )
}

const present = value => value !== undefined && value !== null && value !== ''

const Pairs = ({ pairs, testid }) => (
  <dl className="lms-sent-grid" data-testid={testid}>
    {pairs.map(([key, value, title]) => (
      <React.Fragment key={key}>
        <dt title={title}>{key}</dt>
        <dd>
          <Value value={value} />
        </dd>
      </React.Fragment>
    ))}
  </dl>
)

// The other fields of an lms_read, as readable pairs; nested groups flatten one level.
const GROUPS = ['lms', 'cmi5', 'launch_data', 'query']
const PLAIN = ['mode', 'credit', 'entry', 'credited', 'relay', 'template', 'logic', 'format', 'flavour', 'reason', 'truncated']

const restOf = details => {
  const pairs = []
  PLAIN.forEach(key => present(details[key]) && pairs.push([key, details[key]]))
  if (isObject(details.unload) && present(details.unload.plan)) pairs.push(['unload plan', details.unload.plan])
  GROUPS.forEach(group => {
    const value = details[group]
    if (!isObject(value)) return
    Object.keys(value).forEach(key => present(value[key]) && pairs.push([`${group}.${key}`, value[key]]))
  })
  return pairs
}

const LmsReadView = ({ details }) => {
  const elements = isObject(details.elements) ? Object.entries(details.elements) : []
  const errors = Array.isArray(details.errors) ? details.errors : []
  const rest = restOf(details)
  return (
    <>
      {elements.length > 0 && (
        <>
          <h4>What the LMS holds</h4>
          <table className="table table-sm mb-0" data-testid="lms-sent-elements">
            <thead>
              <tr>
                <th scope="col">Element</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {elements.map(([key, value]) => (
                <tr key={key}>
                  <td>{key}</td>
                  <td>
                    <Value value={value} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {errors.length > 0 && (
        <>
          <h4>LMS errors</h4>
          <ul className="mb-0" data-testid="lms-sent-errors">
            {errors.map((error, index) => (
              <li key={index}>
                <strong>{present(error.code) ? error.code : 'error'}</strong>
                {present(error.string) && ` ${error.string}`}
                {present(error.diagnostic) && ` (${error.diagnostic})`}
                {present(error.fn) && (
                  <>
                    {' '}
                    from <code>{error.fn}({Array.isArray(error.args) ? error.args.map(text).join(', ') : present(error.args) ? text(error.args) : ''})</code>
                  </>
                )}
                {error.threw && ' (it threw)'}
              </li>
            ))}
          </ul>
        </>
      )}
      {rest.length > 0 && (
        <>
          <h4>Other</h4>
          <Pairs pairs={rest} testid="lms-sent-rest" />
        </>
      )}
    </>
  )
}

// Claim names shortened to the part after /claim/, the full name kept in a title.
const shortClaim = name => {
  const at = name.indexOf('/claim/')
  return at >= 0 ? name.slice(at + '/claim/'.length) : name
}

const LtiClaimsView = ({ details }) => {
  const claims = isObject(details.claims) ? details.claims : {}
  const head = [
    ['message_type', details.message_type],
    ['version', details.version],
    ['logic', details.logic]
  ].filter(([, value]) => present(value))
  const pairs = [...head, ...Object.keys(claims).map(name => [shortClaim(name), claims[name], name])]
  return <Pairs pairs={pairs} testid="lms-sent-claims" />
}

// The service names a timeline entry's state by the action without its "lms."
// prefix (session.lms_read); the full action is accepted too.
const KINDS = {
  'session.lms_read': LmsReadView,
  'session.lti_claims': LtiClaimsView,
  'lms.session.lms_read': LmsReadView,
  'lms.session.lti_claims': LtiClaimsView
}

export const LmsSentDetails = ({ entry, index }) => {
  const [open, setOpen] = useState(false)
  const View = KINDS[entry.state]
  if (!View || !isObject(entry.details)) return null
  const id = `lms-sent-${index}`
  return (
    <div className="lms-sent">
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        aria-expanded={open}
        aria-controls={id}
        data-testid={`lms-sent-toggle-${index}`}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Hide what the LMS sent' : 'Show what the LMS sent'}
      </button>
      {open && (
        <div id={id} className="lms-sent-body">
          <View details={entry.details} />
        </div>
      )}
    </div>
  )
}
